import React, { SyntheticEvent, useState } from 'react';
import { FormControl, FormGroup, FormLabel } from 'react-bootstrap';

import { Regions } from '../../../constants/states';
import {
  useDefaultPlateTypeId,
  useHasViewPermission,
  useInput,
  useNumberInput,
  useRequiresPlateType,
} from '../../../hooks';
import { Event } from '../../../interfaces';
import { setEventQueryCache, useCurrentUser } from '../../../queries';
import { EventsService } from '../../../services';
import { isSentToProcessor } from '../../../utils';
import { DisabledWithTooltip } from '../../components/DisabledWithTooltip';
import { Permission } from '../adminOnly';
import { Columns } from '../columns';
import { Flex } from '../flex';
import { H1 } from '../headings';
import { Hr } from '../hr';
import { LicencePlate } from '../licencePlate';
import { Modal } from '../modal';
import { Padded } from '../padded';
import { ProgressButton } from '../progressButton';
import { Spacer } from '../spacer';
import { FormControlWithUppercase, LicencePlateWrapperInModal } from './atoms';
import { LicensePlateTypeDropdownWithNoSave } from './licensePlateTypeDropdown';
import { userGroup } from '../../../enums';
import { ModalContents } from '../modalContents';

interface LPREditProps {
  event: Event;
  onClose: () => void;
  licensePlateUpdate?: (eventPartial: Partial<Event>) => void;
}

export function LicensePlateEditModal(props: LPREditProps) {
  const { event } = props;
  const { license_plate, license_plate_region, id: eventId } = event;

  const [isLoading, setIsLoading] = useState(false);

  const { bind: licensePlate } = useInput<string>(license_plate, 'license_plate');
  const { bind: region } = useInput<string>(license_plate_region ?? '', 'region');
  const { bind: plateTypeId } = useNumberInput<string>(event?.license_plate_type_id ?? '', 'license_plate_type_id');
  const requirePlateType = useRequiresPlateType();
  const defaultLPTypeId = useDefaultPlateTypeId();
  const { data: user } = useCurrentUser();
  const isExternalReviewer = user?.primary_group === userGroup.EXTERNAL_REVIEWER;

  const canWriteLP = useHasViewPermission('events:write:license_plate');

  const handlePlateTypeChange = (eventPartial: Partial<Event>) => {
    plateTypeId.setValue(eventPartial.license_plate_type_id ?? '');
  };

  const onSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canWriteLP) {
      return false;
    }

    setIsLoading(true);

    const eventParts: Partial<Event> = {
      license_plate: licensePlate.value.toUpperCase(),
      license_plate_region: region.value,
    };

    if (requirePlateType) {
      let licensePlateTypeId = plateTypeId.value;

      // for external reviewers, we want to make sure they always have a value so we default it if they haven't selected one
      if (isExternalReviewer) {
        if (!licensePlateTypeId) {
          licensePlateTypeId = defaultLPTypeId;
        }
        eventParts.license_plate_type_id = licensePlateTypeId;
      } else if (licensePlateTypeId) {
        // internally we only save the value if it was explicitly saved by a human
        eventParts.license_plate_type_id = licensePlateTypeId;
      }
    }

    EventsService.updateEvent(eventId, eventParts).then(
      async (res) => {
        setEventQueryCache(res);
        props.licensePlateUpdate?.({ ...eventParts, id: eventId });
        props.onClose();
        setIsLoading(false);
      },
      // err
      () => setIsLoading(false),
    );

    return true;
  };

  const onToggle = () => props.onClose();

  const disabled = event && isSentToProcessor(event);

  return (
    <Modal isOpen onToggle={onToggle} data-test="license-plate-edit-modal" contentLabel="Edit License Plate">
      <ModalContents width="lg">
        <form onSubmit={onSubmit}>
          <Padded>
            <H1>Edit License Plate</H1>
          </Padded>

          <Hr />
          <Padded>
            <Columns cols={[2, 1]}>
              <Flex direction="column">
                <FormGroup>
                  <FormLabel>LPR number</FormLabel>
                  <FormControlWithUppercase
                    {...licensePlate}
                    required
                    autoFocus
                    disabled={disabled}
                    className="hdn-input"
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel>State</FormLabel>
                  <FormControl as="select" {...region} custom disabled={disabled}>
                    <option value="">Please select</option>
                    {Regions.map((r) => (
                      <optgroup label={r.label} key={r.label}>
                        {r.states.map((s) => (
                          <option value={s.region} key={s.region}>
                            {s.state}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </FormControl>
                </FormGroup>

                {requirePlateType && (
                  <FormGroup>
                    <FormLabel>Plate Type</FormLabel>
                    <LicensePlateTypeDropdownWithNoSave
                      event={event}
                      onChange={handlePlateTypeChange}
                      value={plateTypeId.value}
                      license_plate_region={region.value}
                      required={isExternalReviewer}
                    />
                  </FormGroup>
                )}
              </Flex>
              <LicencePlateWrapperInModal>
                <LicencePlate targetWidth={232} event={event} />
              </LicencePlateWrapperInModal>
            </Columns>

            <Spacer />

            <Permission permission="events:write:license_plate">
              <DisabledWithTooltip disabled={disabled}>
                <ProgressButton block isLoading={isLoading} type="submit" disabled={disabled}>
                  Save
                </ProgressButton>
              </DisabledWithTooltip>
            </Permission>
          </Padded>
        </form>
      </ModalContents>
    </Modal>
  );
}
